import React, { Fragment, useContext } from "react";
import {
  Transition,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import styles from "./Drawer.module.css";
import CloseIcon from "~/icons/CloseIcon";
import ThemeContext from "~/contexts/ThemeContext";
import { getLuma } from "~/helpers/colorHelpers";
import { LABELS } from "~/modules/checkout/constants";
import clsx from "clsx";

interface DrawerProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  hideHeader?: boolean;
  dialogClassName?: string;
  drawerClassName?: string;
  contentClassName?: string;
}

const Drawer: React.FC<DrawerProps> = ({
  visible,
  onClose,
  children,
  title,
  hideHeader = false,
  dialogClassName,
  drawerClassName,
  contentClassName,
}) => {
  const { template } = useContext(ThemeContext);
  // Header styles
  const headerBackgroundColor = template.navBar.backgroundColour;
  const fontColor = getLuma(headerBackgroundColor) < 150 ? "#fff" : "#000";

  return (
    <Transition show={visible} as={Fragment} appear>
      <Dialog
        as="div"
        className={clsx(styles.dialog, dialogClassName)}
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter={styles.fadeEnter}
          enterFrom={styles.fadeEnterFrom}
          enterTo={styles.fadeEnterTo}
          leave={styles.fadeLeave}
          leaveFrom={styles.fadeLeaveFrom}
          leaveTo={styles.fadeLeaveTo}
        >
          <div className={styles.backdrop} aria-hidden="true" />
        </TransitionChild>

        <div data-slot="drawer-container" className={styles.container}>
          <div className={styles.panel}>
            <TransitionChild
              as={Fragment}
              enter={styles.slideEnter}
              enterFrom={styles.slideEnterFrom}
              enterTo={styles.slideEnterTo}
              leave={styles.slideLeave}
              leaveFrom={styles.slideLeaveFrom}
              leaveTo={styles.slideLeaveTo}
            >
              <DialogPanel
                data-slot="drawer"
                className={clsx(styles.drawer, drawerClassName)}
              >
                {!hideHeader && (
                  <div
                    className={styles.header}
                    style={{
                      backgroundColor: headerBackgroundColor,
                      color: fontColor,
                    }}
                  >
                    <div></div>
                    {title && <DialogTitle as="h2">{title}</DialogTitle>}
                    <button
                      className={styles.closeButton}
                      onClick={onClose}
                      aria-label={LABELS.CLOSE_DRAWER}
                    >
                      <CloseIcon color={fontColor} />
                    </button>
                  </div>
                )}
                <div
                  data-slot="drawer-content"
                  className={clsx(styles.content, contentClassName)}
                >
                  {children}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Drawer;
